import { ref } from 'vue';

export const useLoadWithRetry = (config = {}) => {
  const maxRetry = config.max_retry || 3;
  const backoff = config.backoff || 1000;

  const isLoaded = ref(false);
  const hasError = ref(false);

  // Retries reuse the same URL, and the browser answers them from its own cache instead of going
  // back to the network — so a first failure keeps failing until a reload. The attachment URL is a
  // redirect Rails marks cacheable, and the 404 behind it is cacheable too, which is exactly what a
  // just-sent image hits: the bubble is rendered before the upload finishes being written. Only the
  // retries get the parameter, so a normal load still benefits from the cache.
  const bustCache = (url, attempt) =>
    `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}`;

  const loadWithRetry = async url => {
    const attemptLoad = attempt => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          isLoaded.value = true;
          hasError.value = false;
          resolve();
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = attempt === 0 ? url : bustCache(url, attempt);
      });
    };

    const sleep = ms => {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    };

    const retry = async (attempt = 0) => {
      try {
        await attemptLoad(attempt);
      } catch (error) {
        if (attempt + 1 >= maxRetry) {
          hasError.value = true;
          isLoaded.value = false;
          return;
        }
        await sleep(backoff * (attempt + 1));
        await retry(attempt + 1);
      }
    };

    await retry();
  };

  return {
    isLoaded,
    hasError,
    loadWithRetry,
  };
};
